import { useEffect, useState } from "react";
import { Alert, Modal, Radio, Select, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { type ConfirmPaymentDocumentIssuance } from "../../hooks/useConfirmManualPayment";

interface ConfirmPaymentModalProps {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  /** `undefined` = domyślne `auto` bez nadpisania — zachowanie sprzed TRE-2006. */
  onConfirm: (documentIssuance?: ConfirmPaymentDocumentIssuance) => void;
}

type DocumentMode = "auto" | "skip";
type DocumentType = "auto" | "receipt" | "invoice";

/**
 * Potwierdzenie płatności manualnej z decyzją o dokumencie (TRE-2006): wystaw
 * automatycznie (z opcjonalnym wymuszeniem paragonu/faktury dla osoby
 * prywatnej) albo nie wystawiaj wcale — wtedy dokument wystawia księgowość
 * z tego samego dropdownu, gdy przelew trafi na konto.
 */
const ConfirmPaymentModal = ({ open, loading, onCancel, onConfirm }: ConfirmPaymentModalProps) => {
  const { t } = useTranslation(["client"]);
  const [mode, setMode] = useState<DocumentMode>("auto");
  const [documentType, setDocumentType] = useState<DocumentType>("auto");

  useEffect(() => {
    if (open) {
      setMode("auto");
      setDocumentType("auto");
    }
  }, [open]);

  const handleOk = () => {
    if (mode === "skip") {
      onConfirm({ mode: "skip" });
      return;
    }

    onConfirm(documentType === "auto" ? undefined : { mode: "auto", typeOverride: documentType });
  };

  return (
    <Modal
      open={open}
      title={t("client:invoice.confirmPaymentTitle")}
      okText={t("client:invoice.markAsPaid")}
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={handleOk}
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Radio.Group value={mode} onChange={(event) => setMode(event.target.value as DocumentMode)}>
          <Space direction="vertical">
            <Radio value="auto">{t("client:invoice.confirmPaymentDocumentAuto")}</Radio>
            <Radio value="skip">{t("client:invoice.confirmPaymentDocumentSkip")}</Radio>
          </Space>
        </Radio.Group>
        {mode === "auto" ? (
          <div>
            <Typography.Text>{t("client:invoice.confirmPaymentDocumentType")}</Typography.Text>
            <Select<DocumentType>
              value={documentType}
              onChange={setDocumentType}
              style={{ width: "100%", marginTop: 8 }}
              options={[
                { value: "auto", label: t("client:invoice.confirmPaymentDocumentTypeAuto") },
                { value: "receipt", label: t("client:invoice.confirmPaymentDocumentTypeReceipt") },
                { value: "invoice", label: t("client:invoice.confirmPaymentDocumentTypeInvoice") },
              ]}
            />
            {documentType !== "auto" && (
              <Alert
                style={{ marginTop: 12 }}
                showIcon
                type="info"
                message={t("client:invoice.confirmPaymentCompanyHint")}
              />
            )}
          </div>
        ) : (
          <Alert showIcon type="info" message={t("client:invoice.confirmPaymentSkipHint")} />
        )}
      </Space>
    </Modal>
  );
};

export default ConfirmPaymentModal;
